import React, { useCallback, useEffect, useState } from 'react';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { Loader2, RefreshCw, HardDriveUpload, CheckCircle2, AlertTriangle } from 'lucide-react';

/**
 * Super-admin dashboard for the audio_mirror_worker backlog. Shows how
 * many completed songs still point at a SUNO / MusicGPT CDN and lets
 * the admin trigger an on-demand sweep of up to 50 songs. Sits at the
 * top of the "Song Health" tab.
 */
export default function AudioMirrorCard({ API, nativeFetchWithAuth }) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sweeping, setSweeping] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await nativeFetchWithAuth(`${API}/admin/audio-mirror/status`, {
        credentials: 'include',
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setStatus(await r.json());
    } catch (e) {
      toast.error(`Failed to load audio mirror status: ${e.message}`);
    } finally {
      setLoading(false);
    }
  }, [API, nativeFetchWithAuth]);

  useEffect(() => { load(); }, [load]);

  const runSweep = async () => {
    if (sweeping) return;
    setSweeping(true);
    const t = toast.loading('Migrating up to 50 songs off ephemeral CDNs…');
    try {
      const r = await nativeFetchWithAuth(`${API}/admin/audio-mirror/sweep-now?limit=50`, {
        method: 'POST',
        credentials: 'include',
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const d = await r.json();
      toast.dismiss(t);
      toast.success(
        `Sweep: migrated ${d.migrated} / attempted ${d.attempted} (${d.failed} failed)`,
        { duration: 8000 }
      );
      await load();
    } catch (e) {
      toast.dismiss(t);
      toast.error(`Sweep failed: ${e.message}`);
    } finally {
      setSweeping(false);
    }
  };

  if (loading && !status) {
    return (
      <div className="glass-card p-4 flex items-center gap-3">
        <Loader2 className="h-5 w-5 animate-spin text-white/60" />
        <span className="text-sm text-white/60">Loading audio mirror status…</span>
      </div>
    );
  }
  if (!status) return null;

  const pending = status.pending_total;
  const dueNow = status.pending_due_now;
  const gaveUp = status.given_up;
  const onHp = status.already_on_hp;
  const busy = pending > 0;

  return (
    <div className="glass-card p-5" data-testid="audio-mirror-card">
      <div className="flex items-start justify-between gap-4 mb-4">
        <div>
          <h3 className="text-base font-bold text-white flex items-center gap-2">
            <HardDriveUpload className="h-4 w-4 text-primary" />
            Audio Mirror to HP Server
          </h3>
          <p className="text-xs text-white/60 mt-1 max-w-xl">
            Songs whose audio is still on a MusicGPT / SUNO CDN get migrated to your HP
            media server by a background worker. Songs on HP download reliably with
            embedded cover art — songs still on CDN fall through to a 302 redirect
            (no cover) or fail if the CDN blocks your server IP.
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Button
            variant="outline" size="sm" onClick={load} disabled={loading}
            className="border-white/20 text-white hover:bg-white/10"
            data-testid="audio-mirror-refresh-btn"
          >
            <RefreshCw className={`h-4 w-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button
            size="sm" onClick={runSweep} disabled={sweeping || dueNow === 0}
            className="bg-purple-600 hover:bg-purple-500 text-white"
            data-testid="audio-mirror-sweep-btn"
          >
            {sweeping
              ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Migrating…</>
              : <><HardDriveUpload className="h-4 w-4 mr-1" /> Sweep 50 now</>}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatBox
          testid="mirror-stat-pending"
          label="Still on CDN"
          value={pending}
          tone={busy ? 'warning' : 'ok'}
        />
        <StatBox
          testid="mirror-stat-due"
          label="Ready to migrate"
          value={dueNow}
          tone={dueNow > 0 ? 'warning' : 'ok'}
        />
        <StatBox
          testid="mirror-stat-hp"
          label="Already on HP"
          value={onHp}
          tone="ok"
          icon={<CheckCircle2 className="h-3 w-3 text-emerald-400" />}
        />
        <StatBox
          testid="mirror-stat-giveup"
          label="Given up"
          value={gaveUp}
          tone={gaveUp > 0 ? 'danger' : 'ok'}
          icon={gaveUp > 0 ? <AlertTriangle className="h-3 w-3 text-amber-400" /> : null}
        />
      </div>

      {status.pending_by_host && Object.keys(status.pending_by_host).length > 0 && (
        <div className="mt-4 pt-4 border-t border-white/10">
          <div className="text-xs text-white/60 mb-2">Pending by source host:</div>
          <div className="flex flex-wrap gap-2">
            {Object.entries(status.pending_by_host).map(([host, n]) => (
              <span key={host} className="text-xs px-2 py-1 rounded-full bg-white/5 border border-white/10 text-white/80">
                <span className="font-mono">{host}</span> · {n}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function StatBox({ label, value, tone = 'ok', icon = null, testid }) {
  const toneCls = {
    ok: 'border-white/10 bg-white/5 text-white',
    warning: 'border-amber-500/30 bg-amber-500/5 text-amber-100',
    danger: 'border-red-500/30 bg-red-500/5 text-red-100',
  }[tone] || 'border-white/10 bg-white/5 text-white';
  return (
    <div className={`rounded-lg border p-3 ${toneCls}`} data-testid={testid}>
      <div className="text-xs opacity-70 flex items-center gap-1">
        {icon}{label}
      </div>
      <div className="text-2xl font-bold mt-1">{value}</div>
    </div>
  );
}
